import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage, HREFLANG_MAP } from "@/contexts/LanguageContext";

const BASE_URL = "https://conciergeriecabassol.fr";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  keywords?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  articleSection?: string;
  noIndex?: boolean;
  /**
   * Per-language path overrides used to build hreflang tags when the route
   * uses different slugs across languages (e.g. blog posts). Paths must be
   * given without the language prefix (e.g. "/blog/luberon-concierge-service").
   */
  alternates?: { fr?: string; en?: string };
}

/** Ensure URL ends with a trailing slash (Cloudflare normalises to `/path/`). */
function withTrailingSlash(url: string): string {
  // Preserve query/hash if any (none in our usage today, defensive).
  const hashIdx = url.indexOf("#");
  const queryIdx = url.indexOf("?");
  const cutIdx = [hashIdx, queryIdx].filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (cutIdx !== undefined) {
    const base = url.slice(0, cutIdx);
    const tail = url.slice(cutIdx);
    return (base.endsWith("/") ? base : base + "/") + tail;
  }
  return url.endsWith("/") ? url : url + "/";
}

export default function SEOHead({
  title,
  description,
  canonicalUrl,
  ogImage = "https://storage.googleapis.com/gpt-engineer-file-uploads/BpAMRF422wZOiOiS1Dr6Ghf51Ct2/social-images/social-1768427174231-Visuel Hero Conciergerie Cabassol.png",
  ogType = "website",
  keywords,
  author = "Conciergerie Cabassol",
  publishedTime,
  modifiedTime,
  articleSection,
  noIndex = false,
  alternates,
}: SEOHeadProps) {
  const { language } = useLanguage();
  const location = useLocation();

  useEffect(() => {
    // Path without language prefix, trailing slash stripped — used to build alternates.
    const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}(\/|$)/, '/').replace(/\/$/, '') || '/';

    // Canonical: always the current /{lang}{path} with trailing slash.
    // (Site is restricted to FR + EN; LanguageContext enforces this.)
    const rawCanonical = canonicalUrl || `${BASE_URL}${location.pathname}`;
    const currentCanonical = withTrailingSlash(rawCanonical);

    // Update document title
    // Blog articles use a short suffix to keep titles under ~60 characters.
    document.title = `${title} | ${ogType === "article" ? "Cabassol" : "Conciergerie Cabassol"}`;

    const setMetaTag = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Dynamic robots directive
    const isIndexable = !noIndex && (language === 'fr' || language === 'en') && !location.search;
    setMetaTag("robots", isIndexable ? "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" : "noindex, follow");

    setMetaTag("description", description);
    if (keywords) setMetaTag("keywords", keywords);
    setMetaTag("author", author);

    document.documentElement.lang = HREFLANG_MAP[language] || language;

    // Open Graph
    setMetaTag("og:title", title, true);
    setMetaTag("og:description", description, true);
    setMetaTag("og:url", currentCanonical, true);
    setMetaTag("og:image", ogImage, true);
    setMetaTag("og:type", ogType, true);
    setMetaTag("og:locale", language === 'en' ? 'en_GB' : 'fr_FR', true);
    setMetaTag("og:site_name", "Conciergerie Cabassol", true);

    // Twitter
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);
    setMetaTag("twitter:image", ogImage);

    // Article specific (for blog posts)
    if (ogType === "article") {
      if (publishedTime) setMetaTag("article:published_time", publishedTime, true);
      if (modifiedTime) setMetaTag("article:modified_time", modifiedTime, true);
      if (articleSection) setMetaTag("article:section", articleSection, true);
      setMetaTag("article:author", author, true);
    }

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = currentCanonical;

    // --- Dynamic hreflang tags (FR + EN only) ---
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    const INDEXED_LANGUAGES: Array<'fr' | 'en'> = ['fr', 'en'];
    const hasAlternates = !!alternates;
    INDEXED_LANGUAGES.forEach((lang) => {
      const altPath = alternates?.[lang];
      // When `alternates` is provided, only emit hreflang for languages explicitly listed
      // (avoids pointing to a non-existent translated URL for orphan blog posts).
      if (hasAlternates && !altPath) return;
      const fallback = pathWithoutLang === '/' ? '' : pathWithoutLang;
      const href = withTrailingSlash(
        `${BASE_URL}/${lang}${altPath ? (altPath.startsWith('/') ? altPath : '/' + altPath) : fallback}`
      );
      const link = document.createElement("link");
      link.rel = "alternate";
      link.hreflang = HREFLANG_MAP[lang] || lang;
      link.href = href;
      document.head.appendChild(link);
    });

    // x-default → French version (only if a FR alternate exists or no alternates map)
    if (!hasAlternates || alternates?.fr) {
      const frAltPath = alternates?.fr;
      const xDefaultHref = withTrailingSlash(
        `${BASE_URL}/fr${frAltPath ? (frAltPath.startsWith('/') ? frAltPath : '/' + frAltPath) : (pathWithoutLang === '/' ? '' : pathWithoutLang)}`
      );
      const xDefault = document.createElement("link");
      xDefault.rel = "alternate";
      xDefault.hreflang = "x-default";
      xDefault.href = xDefaultHref;
      document.head.appendChild(xDefault);
    }

    return () => {
      document.title = "Conciergerie Cabassol - Services Lifestyle Premium en Provence & Côte d'Azur";
      document.documentElement.lang = 'fr';
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());
    };
  }, [title, description, canonicalUrl, ogImage, ogType, keywords, author, publishedTime, modifiedTime, articleSection, noIndex, language, location.pathname, alternates?.fr, alternates?.en]);

  return null;
}
